/**
 * App-level runtime config sourced from Vite env vars.
 *
 * The auth/OIDC + API base config lives in `lib/auth.ts` and `lib/axios.ts`.
 * This module holds the values carried over from the legacy `Web.React` env
 * that the new shell still needs:
 *
 * - `angularBaseUrl` — Old Stack (AngularJS/Razor) host. Pages that link back
 *   to screens not yet replatformed (e.g. an Order detail still served by the
 *   Angular shell) build URLs from this. Legacy name: VITE_REACT_APP_ANGULAR_BASE_URL.
 * - `authMyAccountUri` — FG account-management page; surfaced as the "My account"
 *   link in the sidebar. Legacy name: VITE_REACT_APP_AUTHORITY_FRONT_END_MYACCOUNT_URI.
 * - `authLogoutUri` — FG front-end logout page. When set, sign-out navigates here
 *   (terminates the FG session) instead of the standard OIDC end-session flow.
 *   Legacy name: VITE_REACT_APP_AUTHORITY_FRONT_END_LOGOUT_URI.
 * - `timesheetExternalBaseUrl` — external timesheets system host. Mirrors the Old
 *   Stack's `TimesheetsExternalBaseUrl` config key (`Web/Views/Shared/_Layout.cshtml`),
 *   populated at deploy time from the same pipeline variable. No legacy VITE_* name —
 *   this is new to the New Stack sidebar (see {@link timesheetUrl}).
 */

import { getEnv } from './env';

export const angularBaseUrl = getEnv('VITE_ANGULAR_BASE_URL') ?? '';
export const authMyAccountUri = getEnv('VITE_AUTH_MYACCOUNT_URI') ?? '';
export const authLogoutUri = getEnv('VITE_AUTH_LOGOUT_URI') ?? '';
export const timesheetExternalBaseUrl = getEnv('VITE_TIMESHEET_EXTERNAL_BASE_URL') ?? '';

/** Build a URL into the Old Stack Angular host. No-op safe when unset. */
export function oldStackUrl(path: string): string {
  const base = angularBaseUrl.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * The sidebar's Timesheet link target — mirrors the Old Stack's exact behaviour in
 * `Web/Views/Shared/_Layout.cshtml`: when `TimesheetsExternalBaseUrl` is configured,
 * link out to `{base}/timesheets` (trailing slash trimmed first, same join as the
 * Old Stack); otherwise fall back to the internal Angular route.
 */
export function timesheetUrl(): string {
  if (timesheetExternalBaseUrl === '') {
    return oldStackUrl('/#/timesheets');
  }
  return `${timesheetExternalBaseUrl.replace(/\/$/, '')}/timesheets`;
}
